import { useState } from "react";
import { Link } from "react-router-dom";
import { Watermark } from "./Watermark";
import { TagPersonsModal } from "./TagPersonsModal";
import { can } from "@family/shared";
import type { MediaRef, Role, WatermarkConfig } from "@family/shared";

interface MediaTileProps {
  media: MediaRef;
  familySlug: string;
  role: Role;
  watermarkConfig: WatermarkConfig;
  watermarkVars: {
    user: string;
    family: string;
    phone: string | null;
    email: string | null;
  };
  /** Optional cap (in px) on the rendered media height. Click-to-zoom shows the
   *  full-size image regardless. Defaults to no cap. */
  maxHeight?: number;
}

function TagIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="7" r="4" />
      <path d="M5.5 21a6.5 6.5 0 0 1 13 0" />
    </svg>
  );
}

/** Full-screen lightbox for viewing an image on tap */
function ImageLightbox({ src, onClose, watermarkConfig, watermarkVars }: {
  src: string;
  onClose: () => void;
  watermarkConfig: WatermarkConfig;
  watermarkVars: { user: string; family: string; phone: string | null; email: string | null };
}) {
  // Swipe-to-close on mobile (swipe down)
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [translateY, setTranslateY] = useState(0);

  function handleTouchStart(e: React.TouchEvent) {
    setTouchStart({ x: e.touches[0]!.clientX, y: e.touches[0]!.clientY });
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (!touchStart) return;
    const dy = e.touches[0]!.clientY - touchStart.y;
    if (dy > 0) setTranslateY(dy);
  }

  function handleTouchEnd() {
    if (translateY > 100) {
      onClose();
    }
    setTranslateY(0);
    setTouchStart(null);
  }

  return (
    <div
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: `rgba(0,0,0,${Math.max(0.92 - translateY / 500, 0.3)})`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        cursor: "zoom-out",
        transition: translateY === 0 ? "background 0.2s" : "none",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          background: "rgba(255,255,255,0.15)",
          border: "none",
          color: "#fff",
          borderRadius: "50%",
          width: 40,
          height: 40,
          minHeight: 40,
          fontSize: "1.3rem",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        ✕
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          maxWidth: "100%",
          maxHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: `translateY(${translateY}px)`,
          transition: translateY === 0 ? "transform 0.2s" : "none",
        }}
      >
        <img
          src={src}
          alt=""
          style={{
            maxWidth: "100%",
            maxHeight: "calc(100vh - 32px)",
            objectFit: "contain",
            borderRadius: 4,
            userSelect: "none",
            display: "block",
          }}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
        <Watermark config={watermarkConfig} variables={watermarkVars} />
      </div>
    </div>
  );
}

export function MediaTile({
  media,
  familySlug,
  role,
  watermarkConfig,
  watermarkVars,
  maxHeight,
}: MediaTileProps) {
  const [tagOpen, setTagOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [videoExpanded, setVideoExpanded] = useState(false);
  const canTag = can(role, "media:tag");
  const tagCount = media.taggedPersons.length;

  return (
    <div>
      {/* Lightbox popup */}
      {lightboxOpen && media.kind !== "video" && (
        <ImageLightbox
          src={media.viewUrl}
          onClose={() => setLightboxOpen(false)}
          watermarkConfig={watermarkConfig}
          watermarkVars={watermarkVars}
        />
      )}

      <div
        style={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 4,
          background: "var(--c-bg-alt)",
          cursor: media.kind !== "video" ? "zoom-in" : undefined,
          maxHeight: maxHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={() => {
          if (media.kind !== "video") setLightboxOpen(true);
        }}
      >
        {media.kind === "video" ? (
          videoExpanded ? (
            <video
              src={media.viewUrl}
              controls
              autoPlay
              controlsList="nodownload"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: maxHeight,
                display: "block",
              }}
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : (
            <div
              style={{
                position: "relative",
                width: "100%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => setVideoExpanded(true)}
            >
              {media.thumbnailUrl ? (
                <img
                  src={media.thumbnailUrl}
                  alt="Video thumbnail"
                  loading="lazy"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: maxHeight,
                    display: "block",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: maxHeight ? Math.min(maxHeight, 200) : 200,
                    background: "var(--c-bg-alt)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              )}
              {/* Play button overlay */}
              <span
                aria-label="Play video"
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "rgba(0,0,0,0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <polygon points="8,5 20,12 8,19" />
                  </svg>
                </span>
              </span>
            </div>
          )
        ) : (
          <img
            // Prefer the downscaled thumbnail inline — this tile renders at a
            // few hundred CSS pixels, so the original was multiple megabytes
            // of detail thrown away by the browser's scaler. The lightbox
            // below still loads viewUrl, which is where full resolution is
            // actually wanted. Photos uploaded before thumbnails existed have
            // no thumbnailUrl and fall back to the original until the admin
            // backfill has run over them.
            src={media.thumbnailUrl ?? media.viewUrl}
            alt=""
            loading="lazy"
            // Reserve the right box before the image arrives, so the timeline
            // doesn't reflow as each photo lands.
            width={media.width ?? undefined}
            height={media.height ?? undefined}
            style={{
              width: maxHeight ? "auto" : "100%",
              maxWidth: "100%",
              height: "auto",
              maxHeight: maxHeight,
              display: "block",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
            }}
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
          />
        )}
        <Watermark config={watermarkConfig} variables={watermarkVars} />

        {canTag && (
          <button
            type="button"
            className="media-tile__tag-btn"
            onClick={() => setTagOpen(true)}
            aria-label={
              tagCount > 0
                ? `${tagCount} tagged; edit tags`
                : "Tag people"
            }
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 10px",
              minHeight: 28,
              background: "rgba(0, 0, 0, 0.55)",
              color: "#fff",
              border: "1px solid transparent",
              borderRadius: "var(--r-pill)",
              fontSize: "0.8rem",
              fontWeight: 600,
              cursor: "pointer",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
          >
            <TagIcon />
            {tagCount > 0 ? tagCount : "Tag"}
          </button>
        )}
      </div>

      {media.taggedPersons.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            marginTop: 6,
          }}
        >
          {media.taggedPersons.map((t) => {
            const label = [t.givenName, t.familyName]
              .filter(Boolean)
              .join(" ");
            return (
              <Link
                key={t.tagId}
                to={`/person/${t.id}`}
                className="badge"
                style={{
                  textDecoration: "none",
                  cursor: "pointer",
                }}
                title={label}
              >
                {label}
              </Link>
            );
          })}
        </div>
      )}

      {canTag && (
        <TagPersonsModal
          familySlug={familySlug}
          mediaId={media.id}
          viewUrl={media.viewUrl}
          current={media.taggedPersons}
          open={tagOpen}
          onClose={() => setTagOpen(false)}
        />
      )}
    </div>
  );
}
